/**
 * Forum Service - Handles API calls for the message board
 */

// API URL for the message board Lambda function
const API_URL = 'https://3rlizdd0td.execute-api.us-east-1.amazonaws.com/handle-message-board';

// Helper function to extract the display name from user attributes
const getDisplayName = (userAttributes) => {
  if (!userAttributes) return null;
  return userAttributes['custom:displayName'] || null;
};

const forumService = {
  /**
   * Get all threads from the API
   * @param {string} token - JWT authentication token (required)
   * @returns {Promise<Array>} - Array of thread objects
   */
  async getThreads(token) {
    try {
      if (!token) {
        throw new Error('Authentication token is required');
      }
      
      // Prepare headers with Authorization
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      // Make API call with prepared headers
      const response = await fetch(`${API_URL}?route=getAllThreads`, {
        method: 'GET',
        headers: headers
      });

      // Check if the request was successful
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error fetching threads:', errorData);
        throw new Error('Failed to fetch threads');
      }

      // Parse and return the thread data
      const data = await response.json();
      console.log('Threads API response:', data);
      return data.threads || [];
    } catch (error) {
      console.error('Error in getThreads:', error);
      throw error;
    }
  },

  /**
   * Get a thread with all its posts and comments
   * @param {string} threadId - ID of the thread to fetch
   * @param {string} token - JWT authentication token (required)
   * @returns {Promise<Object>} - Thread object with posts and comments
   */
  async getThreadDetails(threadId, token) {
    try {
      if (!token) {
        throw new Error('Authentication token is required');
      }
      
      // Prepare headers with Authorization
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      // Make API call to get thread details with prepared headers
      const response = await fetch(`${API_URL}?route=getThread&threadId=${threadId}`, {
        method: 'GET',
        headers: headers
      });

      // Check if the request was successful
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error fetching thread details:', errorData);
        throw new Error('Failed to fetch thread details');
      }

      // Parse and return the thread data
      const data = await response.json();
      console.log('Thread details API response:', data);
      return data.thread || null;
    } catch (error) {
      console.error('Error in getThreadDetails:', error);
      throw error;
    }
  },

  /**
   * Create a new thread
   * @param {string} title - Title of the thread
   * @param {string} content - Content of the thread
   * @param {string} token - JWT authentication token
   * @param {string} threadPoster - URL of the thread poster image
   * @param {string} displayName - User's display name
   * @returns {Promise<Object>} - Created thread data
   */
  async createThread(title, content, token, threadPoster = '', displayName = '') {
    try {
      if (!token) {
        throw new Error('Authentication token is required');
      }
      
      // Create request payload
      const payload = {
        action: 'createThread',
        title,
        content,
        threadPoster,
        userHandle: displayName  // Changed from userName to userHandle
      };

      console.log('Thread creation payload:', payload);

      // Make API call with authorization header
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      // Check if the request was successful
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error creating thread:', errorData);
        throw new Error('Failed to create thread');
      }

      // Parse and return the created thread data
      const data = await response.json();
      return data.thread || {};
    } catch (error) {
      console.error('Error in createThread:', error);
      throw error;
    }
  },

  /**
   * Create a new post in a thread
   * @param {string} threadId - ID of the thread
   * @param {string} content - Content of the post
   * @param {string} token - JWT authentication token
   * @param {string} displayName - User's display name
   * @returns {Promise<Object>} - Created post data
   */
  async createPost(threadId, content, token, displayName = '') {
    try {
      if (!token) {
        throw new Error('Authentication token is required');
      }
      
      // Create request payload
      const payload = {
        action: 'createPost',
        threadId,
        content,
        userHandle: displayName  // Changed from userName to userHandle
      };

      console.log('Post creation payload:', payload);

      // Make API call with authorization header
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      // Check if the request was successful
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error creating post:', errorData);
        throw new Error('Failed to create post');
      }

      // Parse and return the created post data
      const data = await response.json();
      return data.post || {};
    } catch (error) {
      console.error('Error in createPost:', error);
      throw error;
    }
  },

  /**
   * Create a new comment on a post
   * @param {string} threadId - ID of the thread
   * @param {string} postId - ID of the post
   * @param {string} content - Content of the comment
   * @param {string} token - JWT authentication token
   * @param {string} displayName - User's display name
   * @returns {Promise<Object>} - Created comment data
   */
  async createComment(threadId, postId, content, token, displayName = '') {
    try {
      if (!token) {
        throw new Error('Authentication token is required');
      }
      
      // Create request payload
      const payload = {
        action: 'createComment',
        threadId,
        postId,
        content,
        userHandle: displayName  // Changed from userName to userHandle
      };

      console.log('Comment creation payload:', payload);

      // Make API call with authorization header
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      // Check if the request was successful
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error creating comment:', errorData);
        throw new Error('Failed to create comment');
      }

      // Parse and return the created comment data
      const data = await response.json();
      return data.comment || {};
    } catch (error) {
      console.error('Error in createComment:', error);
      throw error;
    }
  },

  /**
   * Vote on a thread, post, or comment
   * @param {string} itemId - ID of the item to vote on
   * @param {string} itemType - Type of the item ('thread', 'post', or 'comment')
   * @param {string} voteType - Type of vote ('up' or 'down')
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} - Vote result
   */
  async vote(itemId, itemType, voteType, token) {
    try {
      if (!token) {
        throw new Error('Authentication token is required');
      }
      
      // Create request payload
      const payload = {
        action: 'vote',
        itemId,
        itemType,
        voteType
      };

      console.log(`Voting on ${itemType} ${itemId} with ${voteType} vote`, payload);

      // Make API call with authorization header
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      // Check if the request was successful
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error voting:', errorData);
        throw new Error('Failed to register vote');
      }

      // Parse and return the vote result including the updated item
      const data = await response.json();
      console.log('Vote response:', data);
      return data || {};
    } catch (error) {
      console.error('Error in vote:', error);
      throw error;
    }
  },

  /**
   * Add a message to a thread
   * @param {string} threadId - ID of the thread
   * @param {string} message - Message content
   * @param {string} token - JWT authentication token
   * @param {string} displayName - User's display name
   * @returns {Promise<Object>} - Added message data
   */
  async addMessageToThread(threadId, message, token, displayName = '') {
    try {
      if (!token) {
        throw new Error('Authentication token is required');
      }
      
      // Create request payload
      const payload = {
        action: 'createPost',
        threadId,
        content: message,
        userHandle: displayName  // Changed from userName to userHandle
      };

      console.log('Message creation payload:', payload);

      // Make API call with authorization header
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      // Check if the request was successful
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error adding message to thread:', errorData);
        throw new Error('Failed to add message to thread');
      }

      // Parse and return the added message data
      const data = await response.json();
      return data.post || {};
    } catch (error) {
      console.error('Error in addMessageToThread:', error);
      throw error;
    }
  },

  /**
   * Get all threads created by a specific user
   * @param {string} userId - ID of the user
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} - Array of thread objects
   */
  async getUserThreads(userId, token) {
    try {
      if (!token) {
        throw new Error('Authentication token is required');
      }
      
      // Prepare headers
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      // Make API call to get user's threads
      const response = await fetch(`${API_URL}?route=getUserThreads&userId=${userId}`, {
        method: 'GET',
        headers: headers
      });

      // Check if the request was successful
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error fetching user threads:', errorData);
        throw new Error('Failed to fetch user threads');
      }

      // Parse and return the thread data
      const data = await response.json();
      console.log('User threads API response:', data);
      return data.threads || [];
    } catch (error) {
      console.error('Error in getUserThreads:', error);
      throw error;
    }
  }
};

export default forumService; 